import { BarChartXAxisProps } from '@modules/charts/types';
import { useGetColor } from '@modules/common/hooks';
import { formatNumToCompactNotation } from '@modules/common/utils';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';

const VALUE_LABEL_WIDTH = '55px';

interface BarProps<T> {
  dataPoint: T;
  index: number;
  xAxis: BarChartXAxisProps<T> & {
    domain: [number, number];
  };
  color?: string;
  onMouseEnter?: (event: React.MouseEvent, index: number) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  barSxProps?: SxProps<Theme>;
  labelSxProps?: SxProps<Theme>;
  barHeight?: number | string;
  formatBarValueLabel?: (value: number) => string;
}

export const Bar = <
  T extends {
    id: string;
  },
>({
  dataPoint,
  index,
  xAxis,
  onMouseEnter,
  onMouseLeave,
  color,
  barSxProps,
  labelSxProps,
  barHeight,
  formatBarValueLabel,
}: BarProps<T>) => {
  const getDefaultColor = useGetColor();

  const value = Number(dataPoint[xAxis.dataKey]);

  const [minDomain, maxDomain] = xAxis.domain;

  const barWidthPercent = Math.abs((value / (maxDomain - minDomain)) * 100);

  const isPositive = value >= 0;

  const getLeftPosition = () => {
    if (!isPositive) {
      return;
    }
    const leftPositionPercent = Math.abs(
      (minDomain / (maxDomain - minDomain)) * 100,
    );
    if (minDomain < 0 && leftPositionPercent + barWidthPercent <= 100) {
      return `${leftPositionPercent}%`;
    }
    return 0;
  };

  const getRightPosition = () => {
    if (isPositive) {
      return;
    }
    const rightPositionPercent = Math.abs(
      (maxDomain / (maxDomain - minDomain)) * 100,
    );
    if (maxDomain > 0 && rightPositionPercent + barWidthPercent <= 100) {
      return `${rightPositionPercent}%`;
    }
    return 0;
  };

  return (
    <Box
      overflow="hidden"
      flex={1}
      height={barHeight || '75%'}
      minHeight="10px"
      maxHeight="150px"
      sx={{
        position: 'relative',
      }}
    >
      <Stack
        direction={isPositive ? 'row' : 'row-reverse'}
        alignItems="center"
        width="100%"
        height="100%"
        sx={{
          position: 'absolute',
          left: getLeftPosition(),
          right: getRightPosition(),
          pr: isPositive ? VALUE_LABEL_WIDTH : 0,
          pl: isPositive ? 0 : VALUE_LABEL_WIDTH,
        }}
      >
        <Box
          sx={{
            borderRadius: '3px',
            height: '100%',
            width: `${barWidthPercent}%`,
            minWidth: '3px',
            bgcolor: color ?? getDefaultColor(index, ''),
            ...barSxProps,
          }}
          onMouseEnter={
            onMouseEnter ? (event) => onMouseEnter(event, index) : undefined
          }
          onMouseLeave={onMouseLeave}
        />
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <Typography
            data-role="bar-chart-value-label"
            variant="body2"
            textAlign={isPositive ? 'left' : 'right'}
            sx={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: isPositive ? 0 : undefined,
              right: isPositive ? undefined : 0,
              px: 1,
              ...labelSxProps,
            }}
          >
            {formatBarValueLabel
              ? formatBarValueLabel(value)
              : formatNumToCompactNotation({ value })}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
